<template>
  <div v-show="visible" class="ai-dialog-container">
    <div class="dialog-overlay" @click="$emit('close')"></div>
    <div 
      class="ai-chat-dialog" 
      :class="{ 'dialog-open': visible, 'dialog-fullscreen': isFullscreen, 'dialog-dragging': dragging || resizing }" 
      :style="dialogStyle"
    >
      <div class="dialog-body">
        <ai-session-list
          :sessions="sessions"
          :currentSessionId="currentSessionId"
          :loading="loadingSessions"
          :listWidth="sessionListWidth"
          @create-session="handleCreateSession"
          @select-session="handleSelectSession"
          @delete-session="handleDeleteSession"
          @collapse-change="handleSessionCollapse"
        ></ai-session-list>
        <div
          v-if="!sessionCollapsed"
          class="split-divider"
          @mousedown="handleStartSplitResize"
          @touchstart="handleStartSplitResize"
        ></div>
        <ai-chat-panel
          ref="chatPanel"
          :sessionId="currentSessionId"
          :sessionTitle="currentTitle"
          :isFullscreen="isFullscreen"
          @close="$emit('close')"
          @refresh-sessions="loadSessions"
          @title-updated="handleTitleUpdated"
          @start-drag="handleStartDrag"
          @fullscreen="handleFullscreen"
          @windowed="handleWindowed"
          @request-create-session="handleCreateSessionAsync"
        ></ai-chat-panel>
      </div>
      <div
        v-if="!isFullscreen"
        class="dialog-resize-handle"
        @mousedown="handleStartResize"
        @touchstart="handleStartResize"
      ></div>
    </div>
  </div>
</template>

<script>
import AiSessionList from './AiSessionList.vue'
import AiChatPanel from './AiChatPanel.vue'
export default {
  name: 'AiChatDialog',
  components: { AiSessionList, AiChatPanel },
  props: {
    show: { type: Boolean, default: false },
    buttonX: { type: Number, default: 0 },
    buttonY: { type: Number, default: 0 }
  },
  data() {
    return {
      visible: false,
      sessions: [],
      currentSessionId: '',
      currentTitle: '',
      loadingSessions: false,
      bindSeq: 0, // 自动绑定历史会话的序号，用于取消过期绑定
      dialogPosition: null, // 缓存对话框位置，打开后不再变化
      windowedPosition: null, // 缓存窗口化时的位置，全屏切回时恢复
      isFullscreen: false,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dialogStartLeft: 0,
      dialogStartTop: 0,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 700,
      resizeStartHeight: 560,
      dialogSize: null,
      sessionListWidth: 240,
      sessionCollapsed: false,
      splitResizing: false,
      splitResizeStartX: 0,
      splitResizeStartWidth: 240
    }
  },
  computed: {
    dialogStyle() {
      // 全屏模式：铺满整个视口
      if (this.isFullscreen) {
        return {
          left: '0px',
          top: '0px',
          width: '100vw',
          height: '100vh'
        }
      }
      
      var style = {}
      
      // 如果已经缓存了位置，直接使用
      if (this.dialogPosition) {
        style = Object.assign({}, this.dialogPosition)
      } else {
        // 计算对话框位置，使其跟随浮动按钮且不遮挡按钮
        var dialogWidth = 700;
        var dialogHeight = 560;
        var buttonWidth = 84;
        var buttonHeight = 84;
        
        var top = this.buttonY - dialogHeight;
        if (top < 0) {
          top = this.buttonY + buttonHeight;
        }
        
        var left = this.buttonX + buttonWidth / 2 - dialogWidth / 2;
        left = Math.max(0, Math.min(left, window.innerWidth - dialogWidth));
        top = Math.max(0, Math.min(top, window.innerHeight - dialogHeight));
        
        style = {
          left: left + 'px',
          top: top + 'px'
        };
      }
      
      // 如果有自定义尺寸，合并到样式中
      if (this.dialogSize) {
        style.width = this.dialogSize.width
        style.height = this.dialogSize.height
      }
      
      return style
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.visible = true
        this.loadSessions()
        // 记录本次打开的绑定序号，若期间开启新对话则该次自动绑定会被取消
        var bindSeq = ++this.bindSeq
        // 对话框打开时，缓存当前位置
        this.$nextTick(() => {
          if (!this.dialogPosition) {
            const dialogWidth = 700;
            const dialogHeight = 560;
            const buttonWidth = 84;
            const buttonHeight = 84;
            
            let top = this.buttonY - dialogHeight;
            if (top < 0) {
              top = this.buttonY + buttonHeight;
            }
            
            let left = this.buttonX + buttonWidth / 2 - dialogWidth / 2;
            left = Math.max(0, Math.min(left, window.innerWidth - dialogWidth));
            top = Math.max(0, Math.min(top, window.innerHeight - dialogHeight));
            
            this.dialogPosition = {
              left: left + 'px',
              top: top + 'px'
            }
          }
          
          // 延迟确保有绑定的会话，等待 loadSessions 完成
          setTimeout(() => {
            this.ensureBoundSession(bindSeq)
          }, 300)
        })
      } else {
        this.visible = false
        // 对话框关闭时，清除缓存的位置并重置全屏状态
        this.dialogPosition = null
        this.isFullscreen = false
        this.windowedPosition = null
        this.dialogSize = null
        this.sessionListWidth = 240
        this.sessionCollapsed = false
      }
    }
  },
  beforeDestroy() {
    this.stopDrag()
    this.stopResize()
    this.stopSplitResize()
  },
  methods: {
    handleFullscreen() {
      // 缓存当前窗口化位置
      this.windowedPosition = this.dialogPosition
      this.isFullscreen = true
    },
    handleWindowed() {
      this.isFullscreen = false
      // 恢复之前的窗口化位置
      if (this.windowedPosition) {
        this.dialogPosition = this.windowedPosition
        this.windowedPosition = null
      }
    },
    handleStartDrag(event) {
      // 阻止事件冒泡和默认行为
      event.stopPropagation()
      event.preventDefault()
      
      const isTouch = event.type.startsWith('touch')
      const clientX = isTouch ? event.touches[0].clientX : event.clientX
      const clientY = isTouch ? event.touches[0].clientY : event.clientY
      
      this.dragging = true
      this.dragStartX = clientX
      this.dragStartY = clientY
      
      // 获取当前对话框的位置
      const dialog = this.$el.querySelector('.ai-chat-dialog')
      if (dialog) {
        const rect = dialog.getBoundingClientRect()
        this.dialogStartLeft = rect.left
        this.dialogStartTop = rect.top
        
        // 添加全局事件监听
        document.addEventListener('mousemove', this.onDrag)
        document.addEventListener('mouseup', this.stopDrag)
        document.addEventListener('touchmove', this.onDrag, { passive: false })
        document.addEventListener('touchend', this.stopDrag)
      }
    },
    onDrag(event) {
      if (!this.dragging) return
      
      // 阻止事件冒泡
      event.stopPropagation()
      event.preventDefault()
      
      const isTouch = event.type.startsWith('touch')
      const clientX = isTouch ? event.touches[0].clientX : event.clientX
      const clientY = isTouch ? event.touches[0].clientY : event.clientY
      
      // 计算移动距离
      const deltaX = clientX - this.dragStartX
      const deltaY = clientY - this.dragStartY
      
      // 计算新位置
      let newLeft = this.dialogStartLeft + deltaX
      let newTop = this.dialogStartTop + deltaY
      
      // 边界限制（使用实际对话框尺寸）
      var dialog = this.$el.querySelector('.ai-chat-dialog')
      var dialogWidth = dialog ? dialog.offsetWidth : 700
      var dialogHeight = dialog ? dialog.offsetHeight : 560
      newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - dialogWidth))
      newTop = Math.max(0, Math.min(newTop, window.innerHeight - dialogHeight))
      
      // 更新对话框位置
      this.dialogPosition = {
        left: newLeft + 'px',
        top: newTop + 'px'
      }
    },
    stopDrag() {
      if (!this.dragging) return
      
      this.dragging = false
      
      // 移除全局事件监听
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.stopDrag)
    },
    handleStartResize(event) {
      event.stopPropagation()
      event.preventDefault()
      
      var isTouch = event.type.startsWith('touch')
      var clientX = isTouch ? event.touches[0].clientX : event.clientX
      var clientY = isTouch ? event.touches[0].clientY : event.clientY
      
      this.resizing = true
      this.resizeStartX = clientX
      this.resizeStartY = clientY
      
      var dialog = this.$el.querySelector('.ai-chat-dialog')
      if (dialog) {
        var rect = dialog.getBoundingClientRect()
        this.resizeStartWidth = rect.width
        this.resizeStartHeight = rect.height
        
        document.addEventListener('mousemove', this.onResize)
        document.addEventListener('mouseup', this.stopResize)
        document.addEventListener('touchmove', this.onResize, { passive: false })
        document.addEventListener('touchend', this.stopResize)
      }
    },
    onResize(event) {
      if (!this.resizing) return
      
      event.stopPropagation()
      event.preventDefault()
      
      var isTouch = event.type.startsWith('touch')
      var clientX = isTouch ? event.touches[0].clientX : event.clientX
      var clientY = isTouch ? event.touches[0].clientY : event.clientY
      
      var deltaX = clientX - this.resizeStartX
      var deltaY = clientY - this.resizeStartY
      
      // 最小尺寸为默认对话框大小
      var MIN_WIDTH = 700
      var MIN_HEIGHT = 560
      
      var newWidth = Math.max(MIN_WIDTH, this.resizeStartWidth + deltaX)
      var newHeight = Math.max(MIN_HEIGHT, this.resizeStartHeight + deltaY)
      
      // 不超出视口边界
      var dialog = this.$el.querySelector('.ai-chat-dialog')
      if (dialog) {
        var rect = dialog.getBoundingClientRect()
        newWidth = Math.min(newWidth, window.innerWidth - rect.left)
        newHeight = Math.min(newHeight, window.innerHeight - rect.top)
      }
      
      this.dialogSize = {
        width: newWidth + 'px',
        height: newHeight + 'px'
      }
    },
    stopResize() {
      if (!this.resizing) return
      
      this.resizing = false
      
      document.removeEventListener('mousemove', this.onResize)
      document.removeEventListener('mouseup', this.stopResize)
      document.removeEventListener('touchmove', this.onResize)
      document.removeEventListener('touchend', this.stopResize)
    },
    handleSessionCollapse(collapsed) {
      this.sessionCollapsed = collapsed
    },
    handleStartSplitResize(event) {
      event.stopPropagation()
      event.preventDefault()
      
      var isTouch = event.type.startsWith('touch')
      var clientX = isTouch ? event.touches[0].clientX : event.clientX
      
      this.splitResizing = true
      this.splitResizeStartX = clientX
      this.splitResizeStartWidth = this.sessionListWidth
      
      document.addEventListener('mousemove', this.onSplitResize)
      document.addEventListener('mouseup', this.stopSplitResize)
      document.addEventListener('touchmove', this.onSplitResize, { passive: false })
      document.addEventListener('touchend', this.stopSplitResize)
    },
    onSplitResize(event) {
      if (!this.splitResizing) return
      
      event.stopPropagation()
      event.preventDefault()
      
      var isTouch = event.type.startsWith('touch')
      var clientX = isTouch ? event.touches[0].clientX : event.clientX
      
      var deltaX = clientX - this.splitResizeStartX
      var MIN_WIDTH = 150
      var MAX_WIDTH = 400
      
      var newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, this.splitResizeStartWidth + deltaX))
      this.sessionListWidth = newWidth
    },
    stopSplitResize() {
      if (!this.splitResizing) return
      
      this.splitResizing = false
      
      document.removeEventListener('mousemove', this.onSplitResize)
      document.removeEventListener('mouseup', this.stopSplitResize)
      document.removeEventListener('touchmove', this.onSplitResize)
      document.removeEventListener('touchend', this.stopSplitResize)
    },
    loadSessions() {
      this.loadingSessions = true
      this.wjtHttp.get(this, '/api/ai/chat/session/list', {}, false, (res) => {
        this.loadingSessions = false
        if (res.data.code === 200) {
          this.sessions = res.data.data || []
        }
      }, () => {
        this.loadingSessions = false
      })
    },
    handleCreateSession(successCallback, failCallback) {
      // 不再立即调用后端创建session，避免产生空对话
      // 改为清空当前会话，让面板显示欢迎界面
      // session将在用户发送第一条消息时由handleCreateSessionAsync创建
      this.toNewConversation()
      // 重置消息列表（通知子组件刷新）
      this.$nextTick(() => {
        if (successCallback) successCallback(null)
      })
    },
    // 开启新对话：清空当前会话并复位聊天面板
    startNewConversation() {
      this.toNewConversation()
    },
    toNewConversation() {
      // 使打开对话框时的历史会话自动绑定失效，确保停留在新对话
      this.bindSeq++
      this.currentSessionId = ''
      this.currentTitle = ''
      var panel = this.$refs.chatPanel
      if (panel && panel.resetForNewSession) {
        panel.resetForNewSession()
      }
    },
    handleSelectSession(session) {
      this.currentSessionId = session.sessionId
      this.currentTitle = session.title || '新对话'
    },
    handleDeleteSession(session) {
      var vm = this
      this.$confirm('确定删除该会话吗？删除后不可恢复。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        center: true,
        customClass: 'ai-confirm-dialog',
        appendToBody: true,
        modalAppendToBody: true,
        lockScroll: false,
        zIndex: 99999
      }).then(function() {
        vm.wjtHttp.delete(vm, '/api/ai/chat/session/' + session.sessionId, {}, false, function(res) {
          if (res.data.code === 200) {
            vm.sessions = vm.sessions.filter(function(s) { return s.sessionId !== session.sessionId })
            if (vm.currentSessionId === session.sessionId) {
              vm.currentSessionId = ''
              vm.currentTitle = ''
            }
            vm.$message({ message: '删除成功', type: 'success' })
          }
        })
      }).catch(function() {})
    },
    handleTitleUpdated(newTitle) {
      this.currentTitle = newTitle
      // 更新sessions列表中对应会话的标题
      const session = this.sessions.find(s => s.sessionId === this.currentSessionId)
      if (session) {
        session.title = newTitle
      }
    },
    ensureBoundSession(seq) {
      // 该次自动绑定已过期（例如期间开启了新对话），不再绑定历史会话
      if (seq !== undefined && seq !== this.bindSeq) return
      // 如果没有当前会话ID，尝试绑定最近的会话
      if (!this.currentSessionId) {
        if (this.sessions && this.sessions.length > 0) {
          // 绑定最近的会话（第一个会话）
          this.handleSelectSession(this.sessions[0])
        }
        // 如果没有会话，不自动创建，让面板显示空状态的欢迎提示
        // 用户发送消息时会通过 @request-create-session 触发延迟创建
      }
    },
    handleCreateSessionAsync() {
      // 异步创建会话，返回Promise，用于AiChatPanel的延迟创建
      return new Promise((resolve, reject) => {
        this.wjtHttp.post(this, '/api/ai/chat/session/create', {}, false, (res) => {
          if (res.data.code === 200) {
            var session = res.data.data
            this.currentSessionId = session.sessionId
            this.currentTitle = session.title || '新对话'
            this.sessions.unshift(session)
            // 刷新会话列表，确保数据一致
            this.loadSessions()
            resolve(session)
          } else {
            reject(new Error('创建会话失败'))
          }
        }, () => {
          reject(new Error('创建会话请求失败'))
        })
      })
    }
  }
}
</script>

<style scoped>
.ai-dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
}
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: 1;
}
.ai-chat-dialog {
  position: fixed;
  width: 700px;
  height: 560px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 2;
  opacity: 0;
  transform: translateY(20px) scale(0.95);
  pointer-events: none;
  transition: opacity 0.3s ease, transform 0.3s ease, width 0.3s ease, height 0.3s ease, left 0.3s ease, top 0.3s ease, border-radius 0.3s ease;
}
.dialog-dragging {
  transition: opacity 0.3s ease, transform 0.3s ease, border-radius 0.3s ease !important;
}
.dialog-open {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}
.dialog-fullscreen {
  border-radius: 0 !important;
  box-shadow: none !important;
}
.dialog-body {
  display: flex;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.dialog-fullscreen .dialog-body {
  border-radius: 0 !important;
}
.dialog-resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 20px;
  height: 20px;
  cursor: se-resize;
  z-index: 10;
}
.dialog-resize-handle::before {
  content: '';
  position: absolute;
  right: 5px;
  bottom: 5px;
  width: 8px;
  height: 8px;
  border-right: 2px solid rgba(0, 0, 0, 0.25);
  border-bottom: 2px solid rgba(0, 0, 0, 0.25);
}
.dialog-resize-handle:hover::before {
  border-right: 2px solid rgba(0, 0, 0, 0.5);
  border-bottom: 2px solid rgba(0, 0, 0, 0.5);
}
/* 左侧会话列表 */
.dialog-body > :first-child {
  flex: 0 0 auto;
}
/* 中间分割条 */
.split-divider {
  flex: 0 0 6px;
  cursor: col-resize;
  background: transparent;
  position: relative;
  z-index: 5;
  transition: background 0.2s;
}
.split-divider:hover {
  background: rgba(43, 189, 110, 0.15);
}
.split-divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 20px;
  background: rgba(0, 0, 0, 0.12);
  border-radius: 1px;
}
.split-divider:hover::before {
  background: rgba(43, 189, 110, 0.5);
}
/* 右侧聊天面板：自适应，允许收缩但设置最小宽度 */
.dialog-body > :last-child {
  flex: 1 1 0;
  min-width: 0;
}
::v-deep .ai-confirm-dialog,
::v-deep .el-message-box__wrapper,
::v-deep .v-modal,
::v-deep .el-message-box,
::v-deep .el-message-box__header,
::v-deep .el-message-box__content,
::v-deep .el-message-box__btns {
  z-index: 99999 !important;
}
</style>

